import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    fetchMeta,
    fetchAllEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    uploadImage,
} from "../lib/api";
import { COUNTY_LIST } from "../lib/counties";
import { useAuth } from "../lib/auth";
import { Plus, Pencil, Trash2, ArrowLeft, LogOut, ImagePlus, Loader2, X, Download } from "lucide-react";
import AgeRangePicker from "../components/AgeRangePicker";

const BLANK = {
    title: "",
    description: "",
    county: "Fairfax County",
    city: "",
    venue: "",
    start_date: "",
    end_date: "",
    season: "Spring",
    gender: "Boys",
    age_ranges: ["Youth"],
    event_type: "Tournament",
    organizer: "",
    url: "",
    image_url: null,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [meta, setMeta] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [uploading, setUploading] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    const load = async () => {
        setLoading(true);
        try {
            const ev = await fetchAllEvents();
            setEvents(ev);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeta().then(setMeta);
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(BLANK);
        setOpen(true);
    };
    const openEdit = (evt) => {
        setEditing(evt.id);
        setForm({
            title: evt.title || "",
            description: evt.description || "",
            county: evt.county || "Fairfax County",
            city: evt.city || "",
            venue: evt.venue || "",
            start_date: evt.start_date || "",
            end_date: evt.end_date || "",
            season: evt.season || "Spring",
            gender: evt.gender || "Boys",
            age_ranges:
                Array.isArray(evt.age_ranges) && evt.age_ranges.length
                    ? evt.age_ranges
                    : evt.age_range
                      ? [evt.age_range]
                      : ["Youth"],
            event_type: evt.event_type || "Tournament",
            organizer: evt.organizer || "",
            url: evt.url || "",
            image_url: evt.image_url || null,
        });
        setOpen(true);
    };

    const handleImagePick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Please choose a JPG, PNG, or WebP image");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image must be under 5 MB");
            return;
        }
        setUploading(true);
        try {
            const { url } = await uploadImage(file);
            set("image_url")(url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Upload failed",
            );
        } finally {
            setUploading(false);
        }
    };

    const clearImage = () => set("image_url")(null);

    const save = async () => {
        if (!form.title || !form.start_date || !form.county) {
            toast.error("Title, start date, and county are required");
            return;
        }
        if (!form.age_ranges?.length) {
            toast.error("Pick at least one age range");
            return;
        }
        try {
            if (editing) {
                await updateEvent(editing, form);
                toast.success("Event updated");
            } else {
                await createEvent(form);
                toast.success("Event created");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Failed to save event",
            );
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this event?")) return;
        try {
            await deleteEvent(id);
            toast.success("Event deleted");
            await load();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-white font-manrope"
            data-testid="admin-page"
        >
            <Toaster theme="dark" position="bottom-right" />
            <header className="sticky top-0 z-30 backdrop-blur-2xl bg-black/70 border-b border-white/10">
                <div className="px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-400 hover:text-white"
                        data-testid="back-home"
                    >
                        <ArrowLeft size={14} /> Back
                    </Link>
                    <div className="font-anton text-xl uppercase tracking-widest hidden md:block">
                        Admin · Events
                    </div>
                    <div className="flex gap-2 items-center">
                        {user?.email && (
                            <span
                                className="hidden md:inline text-[10px] tracking-[0.22em] uppercase text-zinc-500 mr-2"
                                data-testid="admin-email"
                            >
                                {user.email}
                            </span>
                        )}
                        <button
                            onClick={openCreate}
                            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF574D] text-white text-xs uppercase tracking-[0.22em] font-semibold px-4 py-2"
                            data-testid="new-event-btn"
                        >
                            <Plus size={14} /> New Event
                        </button>
                        <button
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 text-xs uppercase tracking-[0.22em] font-semibold px-4 py-2"
                            data-testid="logout-btn"
                            title="Sign out"
                        >
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="px-6 md:px-10 py-8">
                <p
                    className="md:hidden text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3"
                    data-testid="admin-mobile-hint"
                >
                    Tap an event to edit · swipe table to see more
                </p>
                <div className="border border-white/10 overflow-x-auto">
                    <table
                        className="w-full min-w-[760px] text-sm"
                        data-testid="events-table"
                    >
                        <thead>
                            <tr className="bg-white/[0.03] border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                <th className="text-left px-4 py-3">Title</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">County</th>
                                <th className="text-left px-4 py-3">Date</th>
                                <th className="text-left px-4 py-3">Season</th>
                                <th className="text-left px-4 py-3">Gender</th>
                                <th className="text-left px-4 py-3">Age</th>
                                <th className="text-left px-4 py-3">Type</th>
                                <th className="text-right px-4 py-3">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && events.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={9}
                                        className="text-center py-10 text-zinc-500 text-xs uppercase tracking-[0.22em]"
                                    >
                                        Loading…
                                    </td>
                                </tr>
                            ) : events.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={9}
                                        className="text-center py-10 text-zinc-500 text-xs uppercase tracking-[0.22em]"
                                    >
                                        No events yet
                                    </td>
                                </tr>
                            ) : (
                                events.map((e) => (
                                    <tr
                                        key={e.id}
                                        onClick={() => openEdit(e)}
                                        className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                                        data-testid={`admin-row-${e.id}`}
                                    >
                                        <td className="px-4 py-3 font-anton uppercase text-base text-white">
                                            {e.title}
                                            {e.source ===
                                                "public_submission" && (
                                                <span className="ml-2 px-1.5 py-0.5 text-[8px] tracking-[0.18em] uppercase border border-white/15 text-zinc-400 align-middle">
                                                    Submitted
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`px-2 py-0.5 text-[9px] tracking-[0.18em] uppercase ${
                                                    e.status === "paid"
                                                        ? "bg-emerald-500/15 text-emerald-400"
                                                        : "bg-yellow-500/15 text-yellow-400"
                                                }`}
                                                data-testid={`status-${e.id}`}
                                            >
                                                {e.status === "paid"
                                                    ? "Live"
                                                    : "Pending Pay"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-zinc-400">
                                            {e.county}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-400">
                                            {e.start_date}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-400">
                                            {e.season}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-400">
                                            {e.gender}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-400">
                                            {(e.age_ranges || (e.age_range ? [e.age_range] : [])).join(" · ")}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-0.5 text-[9px] tracking-[0.18em] uppercase bg-[#FF3B30]/15 text-[#FF3B30]">
                                                {e.event_type}
                                            </span>
                                        </td>
                                        <td
                                            className="px-4 py-3 text-right"
                                            onClick={(ev) => ev.stopPropagation()}
                                        >
                                            <div className="inline-flex gap-2">
                                                {e.image_url && (
                                                    <a
                                                        href={`${BACKEND_URL}${e.image_url}?download=1`}
                                                        download
                                                        className="p-1.5 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300"
                                                        data-testid={`download-image-${e.id}`}
                                                        aria-label="Download image"
                                                        title="Download submitted image"
                                                    >
                                                        <Download size={13} />
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => openEdit(e)}
                                                    className="p-1.5 border border-white/15 hover:border-white text-zinc-300 hover:text-white"
                                                    data-testid={`edit-${e.id}`}
                                                    aria-label="Edit"
                                                >
                                                    <Pencil size={13} />
                                                </button>
                                                <button
                                                    onClick={() => remove(e.id)}
                                                    className="p-1.5 border border-white/15 hover:border-[#FF3B30] text-zinc-300 hover:text-[#FF3B30]"
                                                    data-testid={`delete-${e.id}`}
                                                    aria-label="Delete"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </main>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="rounded-none bg-[#0d0d0d] border-white/15 text-white font-manrope max-w-2xl max-h-[90vh] overflow-y-auto"
                    data-testid="event-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-anton text-3xl uppercase tracking-wide">
                            {editing ? "Edit Event" : "New Event"}
                        </DialogTitle>
                        <DialogDescription className="text-xs tracking-[0.2em] uppercase text-zinc-500">
                            Title, start date, and county are required.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                        <Field label="Title" full>
                            <Input
                                value={form.title}
                                onChange={(e) => set("title")(e.target.value)}
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-title"
                            />
                        </Field>
                        <Field label="County" full>
                            <SelectRaw
                                value={form.county}
                                onChange={set("county")}
                                options={COUNTY_LIST}
                                testId="f-county"
                            />
                        </Field>
                        <Field label="City">
                            <Input
                                value={form.city}
                                onChange={(e) => set("city")(e.target.value)}
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-city"
                            />
                        </Field>
                        <Field label="Venue">
                            <Input
                                value={form.venue}
                                onChange={(e) => set("venue")(e.target.value)}
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-venue"
                            />
                        </Field>
                        <Field label="Start Date">
                            <Input
                                type="date"
                                value={form.start_date}
                                onChange={(e) =>
                                    set("start_date")(e.target.value)
                                }
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-start"
                            />
                        </Field>
                        <Field label="End Date">
                            <Input
                                type="date"
                                value={form.end_date}
                                onChange={(e) =>
                                    set("end_date")(e.target.value)
                                }
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-end"
                            />
                        </Field>
                        <Field label="Season">
                            <SelectRaw
                                value={form.season}
                                onChange={set("season")}
                                options={meta?.seasons || []}
                                testId="f-season"
                            />
                        </Field>
                        <Field label="Gender">
                            <SelectRaw
                                value={form.gender}
                                onChange={set("gender")}
                                options={meta?.genders || []}
                                testId="f-gender"
                            />
                        </Field>
                        <Field label="Age Range (pick one or more) *" full>
                            <AgeRangePicker
                                options={meta?.age_ranges || []}
                                value={form.age_ranges}
                                onChange={set("age_ranges")}
                                testIdPrefix="f-age"
                            />
                        </Field>
                        <Field label="Event Type">
                            <SelectRaw
                                value={form.event_type}
                                onChange={set("event_type")}
                                options={meta?.event_types || []}
                                testId="f-type"
                            />
                        </Field>
                        <Field label="Organizer">
                            <Input
                                value={form.organizer}
                                onChange={(e) =>
                                    set("organizer")(e.target.value)
                                }
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-organizer"
                            />
                        </Field>
                        <Field label="URL">
                            <Input
                                value={form.url}
                                onChange={(e) => set("url")(e.target.value)}
                                placeholder="https://"
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-url"
                            />
                        </Field>
                        <Field label="Description" full>
                            <Textarea
                                value={form.description}
                                onChange={(e) =>
                                    set("description")(e.target.value)
                                }
                                rows={3}
                                className="rounded-none bg-black/40 border-white/15"
                                data-testid="f-desc"
                            />
                        </Field>
                        <Field label="Event Image (optional)" full>
                            <div
                                className="border border-dashed border-white/15 bg-black/30 p-3 flex gap-3 items-start"
                                data-testid="admin-image-picker"
                            >
                                {form.image_url ? (
                                    <div className="relative w-20 h-20 flex-shrink-0 bg-black border border-white/15 overflow-hidden">
                                        <img
                                            src={`${BACKEND_URL}${form.image_url}`}
                                            alt=""
                                            className="w-full h-full object-cover"
                                            data-testid="admin-image-preview"
                                        />
                                        <button
                                            type="button"
                                            onClick={clearImage}
                                            className="absolute top-1 right-1 bg-black/80 hover:bg-[#FF3B30] p-1 text-white transition-colors"
                                            data-testid="admin-image-clear"
                                            aria-label="Remove image"
                                        >
                                            <X size={10} />
                                        </button>
                                        <a
                                            href={`${BACKEND_URL}${form.image_url}?download=1`}
                                            download
                                            className="absolute bottom-1 right-1 bg-black/80 hover:bg-[#FF3B30] p-1 text-white transition-colors"
                                            data-testid="admin-image-download"
                                            aria-label="Download image"
                                            title="Download original"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Download size={10} />
                                        </a>
                                    </div>
                                ) : (
                                    <div className="w-20 h-20 flex-shrink-0 bg-black/40 border border-white/10 flex items-center justify-center text-zinc-600">
                                        <ImagePlus size={22} />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <label
                                        htmlFor="admin-image-input"
                                        className={[
                                            "inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold cursor-pointer transition-colors",
                                            uploading
                                                ? "border-white/15 text-zinc-500 cursor-wait"
                                                : "border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-white",
                                        ].join(" ")}
                                        data-testid="admin-image-label"
                                    >
                                        {uploading ? (
                                            <>
                                                <Loader2
                                                    size={11}
                                                    className="animate-spin"
                                                />
                                                Uploading
                                            </>
                                        ) : (
                                            <>
                                                <ImagePlus size={11} />
                                                {form.image_url
                                                    ? "Replace"
                                                    : "Choose image"}
                                            </>
                                        )}
                                    </label>
                                    <input
                                        id="admin-image-input"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={handleImagePick}
                                        disabled={uploading}
                                        className="hidden"
                                        data-testid="admin-image-input"
                                    />
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1.5">
                                        JPG · PNG · WebP · max 5 MB
                                    </p>
                                </div>
                            </div>
                        </Field>
                    </div>
                    <DialogFooter className="mt-6 gap-2">
                        <button
                            onClick={() => setOpen(false)}
                            className="px-5 py-2 border border-white/15 hover:border-white text-xs uppercase tracking-[0.22em] font-semibold"
                            data-testid="cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={save}
                            className="px-5 py-2 bg-[#FF3B30] hover:bg-[#FF574D] text-white text-xs uppercase tracking-[0.22em] font-semibold"
                            data-testid="save-btn"
                        >
                            {editing ? "Save changes" : "Create event"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Field = ({ label, children, full }) => (
    <div className={full ? "md:col-span-2" : ""}>
        <Label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5 block">
            {label}
        </Label>
        {children}
    </div>
);

const SelectRaw = ({ value, onChange, options, testId }) => (
    <Select value={value} onValueChange={onChange}>
        <SelectTrigger
            data-testid={testId}
            className="rounded-none bg-black/40 border-white/15 text-white"
        >
            <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-none bg-[#0f0f0f] border-white/15 text-white max-h-[300px]">
            {options.map((o) => (
                <SelectItem
                    key={o}
                    value={o}
                    className="rounded-none focus:bg-[#FF3B30]/20 focus:text-white"
                >
                    {o}
                </SelectItem>
            ))}
        </SelectContent>
    </Select>
);

